// Con qué arranca Elementos de marca — ADR-2026-10-22-los-elementos-de-marca-arrancan-con-lo-de-la-marca.
//
// Una marca sin carpetas recibe las tres de siempre (Logos, Elementos gráficos, Tipografías)
// con lo que ya sabemos de ella: el logo de `brand_profiles.logo_url` adentro de "Logos" y
// las tipografías de `brand_profiles.fonts.suggested` (nombres de Google Fonts, los del
// onboarding) bajadas de Google y guardadas en "Tipografías".
//
// Se prepara UNA vez por marca: la señal es que no tenga ninguna carpeta. Corre al terminar el
// onboarding y, para las marcas que ya existían, la primera vez que algo lista sus carpetas
// (Mi marca, "Elegí qué agregar", el agente). Nunca tira: si algo falla, la marca queda como
// estaba o con lo que se pudo.

import { supabase } from '../../lib/supabase';
import { CARPETA_POR_TIPO, uploadBrandAsset, type BrandAssetTipo } from '../../utils/brandAssets';
import { crearCarpetaDeElementos } from './elementosDeMarcaApi';

/** Las del onboarding son 1 o 2 (el prompt de identidad pide eso). */
const MAX_TIPOGRAFIAS = 2;
const MAX_BYTES = 25 * 1024 * 1024;
/**
 * Lo más que espera cada descarga (el logo, la CSS y el archivo de cada tipografía). Son opcionales y
 * la lista de carpetas espera a que terminen: una que se colgaba dejaba Elementos cargando para siempre
 * y trababa al agente (review Greptile del PR #198). Una que se corta se saltea, como una que falla.
 */
const TOPE_DE_DESCARGA_MS = 15_000;
const conTope = () => ({ signal: AbortSignal.timeout(TOPE_DE_DESCARGA_MS) });

export interface MarcaDeLosElementos {
  companyId: string;
  brandId: string;
}

/** `logoGuardado`: el logo quedó de verdad en "Logos" (bajarlo o subirlo pudo fallar). */
interface Preparacion {
  preparada: boolean;
  logoGuardado: boolean;
}
const NADA: Preparacion = { preparada: false, logoGuardado: false };

const enCurso = new Map<string, Promise<Preparacion>>();

/** Dos llamadas a la vez (Mi marca y el chat montan juntos) comparten la misma. */
function prepararUnaVez(marca: MarcaDeLosElementos): Promise<Preparacion> {
  const clave = `${marca.companyId}:${marca.brandId}`;
  const previa = enCurso.get(clave);
  if (previa) return previa;
  const promesa = preparar(marca)
    .catch((e) => {
      console.warn('[elementosIniciales] no se pudo preparar', e);
      return NADA;
    })
    .finally(() => enCurso.delete(clave));
  enCurso.set(clave, promesa);
  return promesa;
}

/** Prepara Elementos de marca si la marca no tiene ninguna carpeta. Devuelve `true` si la preparó ahora. */
export function prepararElementosDeLaMarca(marca: MarcaDeLosElementos): Promise<boolean> {
  return prepararUnaVez(marca).then((r) => r.preparada);
}

/**
 * Carga las carpetas y, si no hay ninguna, prepara la marca y las vuelve a cargar. Para
 * `fetchCarpetasDeElementos` y `listarCarpetasDeElementos`.
 */
export async function conElementosIniciales<T>(marca: MarcaDeLosElementos, cargar: () => Promise<T[]>): Promise<T[]> {
  const lista = await cargar();
  if (lista.length > 0) return lista;
  return (await prepararElementosDeLaMarca(marca)) ? cargar() : lista;
}

/**
 * Un logo nuevo de Mi marca se SUMA a "Logos" (no reemplaza al anterior). Si la marca todavía
 * no tenía Elementos de marca, prepararla ya copia este logo (el `logo_url` ya es el nuevo).
 */
export async function sumarLogoAElementos(marca: MarcaDeLosElementos, file: File): Promise<void> {
  try {
    // Preparar copia el logo nuevo, pero bajarlo o subirlo puede fallar: sólo si quedó de verdad no
    // hace falta el archivo que llegó (review Greptile del PR #198). Sin carpeta, cae en "Logos".
    if ((await prepararUnaVez(marca)).logoGuardado) return;
    await uploadBrandAsset({ file, brandProfileId: marca.brandId, companyId: marca.companyId, tipo: 'logo' });
  } catch (e) {
    console.warn('[elementosIniciales] no se pudo sumar el logo', e);
  }
}

async function tieneCarpetas(brandId: string): Promise<boolean> {
  const { data, error } = await supabase.from('brand_asset_carpetas').select('id').eq('brand_id', brandId).limit(1);
  // Sin poder leer, se hace como si tuviera: mejor no preparar que duplicar carpetas.
  if (error) return true;
  return (data ?? []).length > 0;
}

async function preparar({ companyId, brandId }: MarcaDeLosElementos): Promise<Preparacion> {
  if (await tieneCarpetas(brandId)) return NADA;

  const { data: fila, error } = await supabase
    .from('brand_profiles')
    .select('name, logo_url, fonts')
    .eq('id', brandId)
    .eq('id_company', companyId)
    .maybeSingle();
  if (error || !fila) return NADA;
  const marca = fila as { name: string | null; logo_url: string | null; fonts: unknown };

  // Se baja todo ANTES de crear las carpetas: si la pestaña se cierra en el medio, es más
  // probable que no quede nada a que queden las carpetas sin el logo.
  const [logo, tipografias] = await Promise.all([
    marca.logo_url ? bajarLogo(marca.logo_url, marca.name).catch(() => null) : Promise.resolve(null),
    bajarTipografias(familiasDeLaMarca(marca.fonts)),
  ]);

  // Lo de arriba tarda unos segundos: otra pestaña pudo haberla preparado mientras.
  if (await tieneCarpetas(brandId)) return NADA;

  // En orden inverso: la grilla muestra la más nueva primero, y así queda
  // Logos, Elementos gráficos, Tipografías (como el botón de la pantalla vacía).
  const carpetas = new Map<BrandAssetTipo, string>();
  for (const tipo of ['font', 'element', 'logo'] as const) {
    const destino = CARPETA_POR_TIPO[tipo]!;
    const c = await crearCarpetaDeElementos({ companyId, brandId, nombre: destino.nombre, color: destino.color });
    carpetas.set(tipo, c.id);
  }

  const subir = async (file: File, tipo: BrandAssetTipo): Promise<boolean> => {
    try {
      await uploadBrandAsset({ file, brandProfileId: brandId, companyId, tipo, carpetaId: carpetas.get(tipo) });
      return true;
    } catch (e) {
      console.warn(`[elementosIniciales] no se pudo guardar «${file.name}»`, e);
      return false;
    }
  };
  const logoGuardado = logo ? await subir(logo, 'logo') : false;
  for (const t of tipografias) await subir(t, 'font');
  return { preparada: true, logoGuardado };
}

/** "Logo de Acme.png". La extensión sale del tipo: la URL del favicon no siempre la trae. */
async function bajarLogo(url: string, nombreDeLaMarca: string | null): Promise<File | null> {
  const res = await fetch(url, conTope());
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!blob.type.startsWith('image/') || blob.size === 0 || blob.size > MAX_BYTES) return null;
  const ext = extensionDeImagen(blob.type);
  const marca = (nombreDeLaMarca ?? '').trim();
  return new File([blob], `${marca ? `Logo de ${marca}` : 'Logo'}.${ext}`, { type: blob.type });
}

export function extensionDeImagen(mime: string): string {
  const sub = mime.split('/')[1]?.split(';')[0]?.trim().toLowerCase() ?? '';
  if (sub === 'jpeg') return 'jpg';
  if (sub === 'svg+xml') return 'svg';
  if (sub === 'x-icon' || sub === 'vnd.microsoft.icon') return 'ico';
  return sub.replace(/[^a-z0-9]/g, '') || 'png';
}

/** Los nombres de `brand_profiles.fonts.suggested`, sin repetir y sólo los que pueden ser una
 *  familia de Google Fonts (letras, números y espacios). */
export function familiasDeLaMarca(fonts: unknown): string[] {
  const sugeridas = (fonts as { suggested?: unknown } | null)?.suggested;
  if (!Array.isArray(sugeridas)) return [];
  const vistas = new Set<string>();
  const out: string[] = [];
  for (const s of sugeridas) {
    if (typeof s !== 'string') continue;
    const familia = s.trim().replace(/\s+/g, ' ');
    if (!familia || familia.length > 60 || !/^[\p{L}\p{N} ]+$/u.test(familia)) continue;
    const clave = familia.toLowerCase();
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    out.push(familia);
    if (out.length === MAX_TIPOGRAFIAS) break;
  }
  return out;
}

async function bajarTipografias(familias: string[]): Promise<File[]> {
  const archivos = await Promise.all(familias.map((f) => bajarDeGoogleFonts(f).catch(() => null)));
  return archivos.filter((a): a is File => a !== null);
}

/**
 * El archivo de una familia de Google Fonts, peso normal. La CSS trae un `@font-face` por
 * subconjunto de caracteres; el `latin` cubre el castellano (á, ñ, ¿). Una familia que Google no
 * tiene (las que sugiere el análisis no siempre son de Google, como "Helvetica Now") da 400 y
 * se saltea.
 */
async function bajarDeGoogleFonts(familia: string): Promise<File | null> {
  const css = await fetch(`https://fonts.googleapis.com/css2?family=${encodeURIComponent(familia).replace(/%20/g, '+')}`, conTope());
  if (!css.ok) return null;
  const url = urlDelArchivoDeFuente(await css.text());
  if (!url) return null;
  const res = await fetch(url, conTope());
  if (!res.ok) return null;
  const blob = await res.blob();
  if (blob.size === 0 || blob.size > MAX_BYTES) return null;
  const ext = url.match(/\.(woff2|woff|ttf|otf)(?:$|\?)/i)?.[1]?.toLowerCase() ?? 'woff2';
  return new File([blob], `${familia}.${ext}`, { type: `font/${ext}` });
}

/** La URL del archivo del bloque `latin`; si la CSS no separa por subconjuntos, la primera. */
export function urlDelArchivoDeFuente(css: string): string | null {
  const limpiar = (u: string) => u.trim().replace(/^['"]|['"]$/g, '');
  const latin = css.match(/\/\*\s*latin\s*\*\/\s*@font-face\s*\{[^}]*?src:\s*url\(([^)]+)\)/);
  if (latin) return limpiar(latin[1]);
  const primera = css.match(/src:\s*url\(([^)]+)\)/);
  return primera ? limpiar(primera[1]) : null;
}
